// src/hooks/useUsageTracker.ts
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import type { AppUser } from './useHRData';

/**
 * useUsageTracker — client-side menu/subtab usage tracking (2026-10-22).
 *
 * Owner request: "see which menus and subtabs the users use the most" so the
 * least-used surfaces can be prioritized for UX work. Every sidebar menu click
 * IS a route change (AppNav navigates to `/{tabId}`), and every subtab click
 * updates a URL search param (useFilterParam — ?tab=, ?subTab=, …). So ONE
 * route/search observer at the app shell captures both — no per-component
 * instrumentation needed.
 *
 * WHAT IS TRACKED (privacy by design, GDPR Eintrag 13):
 *   - tenant_id, user_id, role snapshot — internal identifiers only; names/
 *     emails are resolved at RENDER time from the users list, never stored.
 *   - menu  = first non-empty path segment ('dashboard', 'reservations', …).
 *   - subtab = the value of an allowlisted NAVIGATION param (below). Search
 *     terms (q/hkQ/resQ), page numbers and date filters are deliberately NOT
 *     tracked — they are filters, not navigation, and would add noise.
 *   Guest data of ANY kind never reaches this table.
 *
 * THROTTLE: a batched queue flushes (a) when 3 events are pending, (b) every
 * 15 s via an interval WITH teardown (AGENTS.md §2), and (c) on pagehide.
 * Consecutive identical (menu, subtab) states are suppressed so re-renders
 * and replace-navigations do not inflate counts. Errors are swallowed —
 * tracking must never break UX.
 *
 * RLS: the insert policy `app_usage_events_insert_self` only admits staff
 * rows for the caller's own user_id + tenant (migration 20261022140000).
 */

/** Navigation-only URL params worth tracking. Mirrors useClearStaleParams
 *  registry; date ranges / search terms are intentionally excluded. */
const TRACKED_PARAMS = [
    'tab',        // haustechnik / analytics / automations / inventory
    'subTab',     // sales
    'companyTab', // sales
    'view',       // housekeeping (list views)
    'check',      // internal-resources (active check instance)
    'department', // traces (department filter = the meaningful nav signal)
    'month',      // kassenbuch
];

const MENU_MAX_LEN = 60;
const SUBTAB_MAX_LEN = 60;
const FLUSH_COUNT = 3;
const FLUSH_INTERVAL_MS = 15_000;
/** Same (menu, subtab) state within this window is treated as one event. */
const DEDUPE_WINDOW_MS = 3_000;

type PendingEvent = {
    tenant_id: string;
    user_id: string;
    role: string | null;
    menu: string;
    subtab: string | null;
};

export function useUsageTracker(user: AppUser | null): void {
    const location = useLocation();
    const userRef = useRef<AppUser | null>(null);
    // Sync the ref OUTSIDE render (React Compiler lint forbids ref access
    // during render) — the user identity is stable, so this only runs once.
    useEffect(() => {
        userRef.current = user;
    }, [user?.id, user?.tenantId, user?.role, user]);

    const queueRef = useRef<PendingEvent[]>([]);
    const lastKeyRef = useRef<{ key: string; at: number }>({ key: '', at: 0 });
    const trackingOffRef = useRef(false);

    const flushQueue = useCallback(() => {
        if (queueRef.current.length === 0) return;
        const batch = queueRef.current;
        queueRef.current = [];
        // Fire-and-forget: a failed analytics insert must never surface in the
        // UI. RLS still guards the write server-side.
        supabase
            .from('app_usage_events')
            .insert(batch)
            .then(({ error }) => {
                if (error) {
                    // Re-queue once on failure would risk an unbounded loop;
                    // drop the batch instead — analytics is best-effort.
                    if (import.meta.env.DEV) console.debug('[usageTracker] insert failed:', error.message);
                }
            });
    }, []);

    useEffect(() => {
        const u = userRef.current;
        if (!u?.id || !u.tenantId) return;
        if (trackingOffRef.current) return;

        // menu = first non-empty path segment ('/traces/abc' → 'traces',
        // '/' → 'dashboard'). Cap the length defensively.
        const firstSegment = location.pathname.split('/').filter(Boolean)[0];
        const menu = (firstSegment || 'dashboard').slice(0, MENU_MAX_LEN);

        // subtab = first allowlisted nav param present in the URL.
        const params = new URLSearchParams(location.search);
        let subtab: string | null = null;
        for (const key of TRACKED_PARAMS) {
            const value = params.get(key);
            if (value) {
                subtab = value.slice(0, SUBTAB_MAX_LEN);
                break;
            }
        }

        const key = `${menu}|${subtab ?? ''}`;
        const now = Date.now();
        if (key === lastKeyRef.current.key && now - lastKeyRef.current.at < DEDUPE_WINDOW_MS) {
            return; // same state within the dedupe window — not a new navigation
        }
        lastKeyRef.current = { key, at: now };

        queueRef.current.push({
            tenant_id: u.tenantId,
            user_id: u.id,
            role: u.role ?? null,
            menu,
            subtab,
        });

        if (queueRef.current.length >= FLUSH_COUNT) {
            flushQueue();
        }
        // flushQueue is a useCallback with [] deps — identity-stable, so this
        // dep never re-runs the effect (pure linter satisfaction).
    }, [location.pathname, location.search, flushQueue]);

    useEffect(() => {
        trackingOffRef.current = !user?.id || !user?.tenantId;
    }, [user?.id, user?.tenantId]);

    // Flush timer + pagehide handler — BOTH torn down on unmount (P.12).
    useEffect(() => {
        const onUnload = () => flushQueue();
        window.addEventListener('pagehide', onUnload);
        const interval = setInterval(() => {
            if (queueRef.current.length > 0) flushQueue();
        }, FLUSH_INTERVAL_MS);
        return () => {
            window.removeEventListener('pagehide', onUnload);
            clearInterval(interval);
            // Final best-effort flush so a quick session still persists.
            flushQueue();
        };
    }, [flushQueue]);
}
